import type { AudienceInput, CoverageReport, MakerAgent } from '@ralph/contracts'
import { liveAudienceOf } from '@ralph/contracts'
import { Lock, TriangleAlert } from 'lucide-react'
import { AudiencePicker } from '@/components/groups/AudiencePicker.tsx'
import type { PickerViewer } from '@/components/groups/audience-picker.ts'
import { BuilderSection, Note, SECTION_IDS } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import { coverageGaps } from '@/components/marketplace/maker/knowledge-coverage.ts'
import { ChoiceRow } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'

/**
 * Who can use it, and whether they may copy it.
 *
 * The picker is the one every surface uses (PRD-26 D-30): kind radios, groups as chips, the reach
 * line with the change from now, and the review line — predicted with the rule the server decides
 * with, so "Legal is not a group you manage, so an administrator will review adding it" is what
 * publishing will actually do. It edits a choice, not the agent: who can use it changes only at
 * publication, which is the step that shows the maker what they are sharing and records their
 * acknowledgement (AGM-042).
 *
 * Under the choice, a coverage summary (GRP-045): which attached bases will not reach whom, for the
 * audience being chosen — "Payroll Rules will not reach Legal (8 people) — see Knowledge". Knowledge
 * (step 3) holds the detail and the way to fix it.
 */
export const AudienceSection = ({
  step,
  agent,
  audience,
  onAudienceChange,
  viewer,
  privateLabel,
  coverage,
  draft,
  disabled,
  canChangeAudience,
  onChange
}: {
  step: number
  agent: MakerAgent
  audience: AudienceInput
  onAudienceChange: (audience: AudienceInput) => void
  viewer: PickerViewer
  /** "Only me", "Only Finance EMEA’s managers" (D-37). */
  privateLabel: string
  coverage: CoverageReport | undefined
  draft: BuilderDraft
  disabled: boolean
  canChangeAudience: boolean
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  // The gap summary counts people and joins group names, both of which are the reader's
  // language's business — so the locale comes from the hook here rather than from a module-scope
  // capture inside the pure module that words it (PRD-27 F-3).
  const i18n = useI18n()
  const strings = i18n.t.marketplace.maker.sections.audience
  const copyStrings = i18n.t.marketplace.maker.copyPolicy
  const attached = new Set(draft.knowledgeBaseIds)
  const gaps = coverageGaps(
    i18n.t.marketplace.maker,
    i18n,
    (coverage?.bases ?? []).filter((entry) => attached.has(entry.baseId))
  )

  return (
    <BuilderSection
      id={SECTION_IDS.audience}
      step={step}
      title={strings.title}
      lead={viewer.isAdmin ? strings.leadAdmin : strings.lead}
    >
      {!canChangeAudience && (
        <Note tone="info" icon={<Lock size={13} strokeWidth={2} />}>
          {strings.locked}
        </Note>
      )}

      <AudiencePicker
        mode="item"
        value={audience}
        onChange={onAudienceChange}
        live={liveAudienceOf(agent.audience)}
        // Nobody uses an agent that is not live: its stored groups are what the review rule
        // compares against (D-57), not people it is "shared now" with.
        inForce={agent.state === 'published' ? liveAudienceOf(agent.audience) : null}
        known={agent.audience.groups}
        viewer={viewer}
        privateLabel={privateLabel}
        reachItem={{ kind: 'agent', id: agent.agentId }}
        consequence={i18n.t.groups.consequence.agent}
        commitVerb="publish"
        disabled={disabled || !canChangeAudience}
      >
        {gaps.length > 0 && (
          <ul className="mt-3 space-y-1">
            {gaps.map((gap) => (
              // Icon and words, never the colour on its own (AGM-124).
              <li key={gap.baseId} className="flex items-start gap-1.5 text-[12.5px] leading-[1.5] text-alert">
                <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
                <span className="min-w-0">
                  {strings.gap(gap.base, gap.whom)} —{' '}
                  <a href={`#${SECTION_IDS.knowledge}`} className="underline underline-offset-2">
                    {strings.seeKnowledge}
                  </a>
                </span>
              </li>
            ))}
          </ul>
        )}
      </AudiencePicker>

      <div>
        <span className="eyebrow mb-1.5 block text-fg-muted">{copyStrings.title}</span>
        <p className="mb-2.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{copyStrings.lead}</p>
        <ChoiceRow
          name="copy-policy"
          value={draft.copyPolicy}
          disabled={disabled}
          options={[
            { value: 'auto', label: copyStrings.auto, hint: copyStrings.autoHint },
            { value: 'allowed', label: copyStrings.allowed, hint: copyStrings.allowedHint },
            { value: 'blocked', label: copyStrings.blocked, hint: copyStrings.blockedHint }
          ]}
          onChange={(copyPolicy) => onChange({ copyPolicy })}
        />
      </div>
    </BuilderSection>
  )
}
